// src/models/Campaign.js - Updated with dispatch tracking fields
// Data model for marketing campaign management

export class Campaign {
  constructor(data = {}) {
    this.id = data.id || this.generateId('campaign');
    this.name = data.name || '';
    this.targetAudience = data.targetAudience || '';
    this.type = data.type || 'One-Time'; // One-Time
    this.templateId = data.templateId || '';
    this.templateVariables = data.templateVariables || {}; // User-defined variable values

    // AI-generated targeting fields (persistent)
    this.sqlQuery = data.sqlQuery || '';
    this.aiInterpretation = data.aiInterpretation || '';
    this.customers = data.customers || [];

    // Dispatch tracking fields
    this.lastDispatchDate = data.lastDispatchDate || ''; // Last successful dispatch date
    this.totalDispatches = data.totalDispatches || 0; // Total number of dispatches
    this.totalMessagesSent = data.totalMessagesSent || 0; // Total messages sent across all dispatches
    this.lastDispatchStatus = data.lastDispatchStatus || ''; // completed, failed, processing
    this.lastDispatchMessageCount = data.lastDispatchMessageCount || 0; // Messages sent in last dispatch

    // Payment info
    this.paymentId = data.paymentId || '';
    this.paymentUrl = data.paymentUrl || '';
    this.paymentStatus = data.paymentStatus || '';
    this.expiresAt = data.expiresAt || '';
    
    // Timestamps
    this.created_at = data.created_at || new Date().toISOString();
    this.updated_at = data.updated_at || new Date().toISOString();
  }

  generateId() {
    return 'campaign_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  // Create campaign instance from object
  static fromObject(data) {
    return new Campaign(data);
  }

  // Update campaign data
  update(newData) {
    Object.keys(newData).forEach(key => {
      if (key !== 'id' && key !== 'created_at') {
        this[key] = newData[key];
      }
    });
    this.updated_at = new Date().toISOString();
    return this;
  }


  // NEW: Update dispatch tracking after successful dispatch
  updateDispatchTracking(messagesSent, dispatchDate = null) {
    this.lastDispatchDate = dispatchDate || new Date().toISOString().split('T')[0];
    this.lastDispatchStatus = 'completed';
    this.lastDispatchMessageCount = messagesSent;
    this.totalDispatches += 1;
    this.totalMessagesSent += messagesSent;

    this.updated_at = new Date().toISOString();
    return this;
  }

  // NEW: Mark dispatch as failed
  markDispatchFailed() {
    this.lastDispatchStatus = 'failed';
    this.updated_at = new Date().toISOString();
    return this;
  }

  // Convert to plain object for Firestore
  toObject() {
    return {
      id: this.id,
      name: this.name,
      targetAudience: this.targetAudience,
      type: this.type,
      templateId: this.templateId,
      templateVariables: this.templateVariables,
      sqlQuery: this.sqlQuery,
      aiInterpretation: this.aiInterpretation,
      customers: this.customers,  
      lastDispatchDate: this.lastDispatchDate,
      nextDispatchDate: this.nextDispatchDate,
      totalDispatches: this.totalDispatches,
      totalMessagesSent: this.totalMessagesSent,
      lastDispatchStatus: this.lastDispatchStatus,
      lastDispatchMessageCount: this.lastDispatchMessageCount,
      created_at: this.created_at,
      updated_at: this.updated_at
    };
  }

  // Validation methods
  static validate(data) {
    const errors = [];

    if (!data.name || data.name.trim().length === 0) {
      errors.push('Campaign name is required');
    }

    if (data.name && data.name.trim().length > 100) {
      errors.push('Campaign name must be less than 100 characters');
    }

    // Target audience is now optional - users can leave it empty and update later
    // or use the "All Customers" option

    if (!data.type || !this.isValidCampaignType(data.type)) {
      errors.push('Valid campaign type is required');
    }

    if (!data.templateId || data.templateId.trim().length === 0) {
      errors.push('Template selection is required');
    }

    // Validate user-defined variables
    if (data.templateId) {
      const { getUserDefinedVariables, getCampaignTemplate } = require('../config/templates');
      const userVars = getUserDefinedVariables(data.templateId);

      userVars.forEach(variable => {
        const value = data.templateVariables?.[variable.name];
        if (variable.required && (!value || value.trim().length === 0)) {
          errors.push(`${variable.displayName} is required`);
        }
      });
    }

    // Validate AI interpretation fields (optional but if present, should be complete)
    // Note: Manual selection has aiInterpretation but no sqlQuery, and customers array
    // AI/All selection has both aiInterpretation and sqlQuery
    const hasManualSelection = data.aiInterpretation && !data.sqlQuery && data.customers && data.customers.length > 0;

    if (data.aiInterpretation && !data.sqlQuery && !hasManualSelection) {
      errors.push('SQL query or customer selection is required when AI interpretation is present');
    }

    if (data.sqlQuery && !data.aiInterpretation) {
      errors.push('AI interpretation is required when SQL query is present');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  static isValidCampaignType(type) {
    const validTypes = ['One-Time'];
    return validTypes.includes(type);
  }

  // Get available campaign types with display names
  static getCampaignTypes() {
    return [
      { value: 'One-Time', label: 'One-Time' },
    ];
  }

  // Check if campaign has AI-generated targeting
  hasAITargeting() {
    return !!(this.sqlQuery && this.aiInterpretation);
  }

  isDueForDispatch() {
    if (this.type === 'One-Time') {
      return false; // One-time campaigns are now manual only
    }

    return false;
  }

  // NEW: Get dispatch status display information
  getDispatchStatusDisplay() {
    if (!this.lastDispatchStatus) {
      return { label: 'Never dispatched', color: 'gray', icon: '⏳' };
    }

    const statusMap = {
      completed: { label: 'Last dispatch successful', color: 'green', icon: '✅' },
      failed: { label: 'Last dispatch failed', color: 'red', icon: '❌' },
      processing: { label: 'Currently dispatching', color: 'blue', icon: '⚡' }
    };

    return statusMap[this.lastDispatchStatus] || statusMap.completed;
  }

  // NEW: Format last dispatch date for display
  formatLastDispatchDate() {
    if (!this.lastDispatchDate) return 'Never';
    return new Date(this.lastDispatchDate).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  // Get truncated AI interpretation for display
  getTruncatedInterpretation(maxLength = 100) {
    if (!this.aiInterpretation) return '';
    if (this.aiInterpretation.length <= maxLength) return this.aiInterpretation;
    return this.aiInterpretation.substring(0, maxLength) + '...';
  }
}